import { styled } from "@mui/material";
import { useEffect, useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { createGif, upload } from "utils/gifUploader";
import HorizontalLabelPositionBelowStepper from "../../UI/Stepper";
import UploadFifthStep from "./UploadFifthStep";
import UploadFirstStep from "./UploadFirstStep";
import UploadFourthStep from "./UploadFourthStep";
import UploadSecondStep from "./UploadSecondStep";
import { UploadBulkImgStep } from "./UploadBulkImgStep";
import UploadSelectGifType from "./UploadSelectGifType";
import { uploadFile } from "../../../utils/upload";
import { useNavigate } from "react-router-dom";
import colors from "../../../theme/themeConstants";
import { createGallery } from "utils/galleryUploader";
import { useMeQuery } from "queries";
import { apiMain } from "services";

export interface IFile {
  url: string;
  uploadProgress: number;
  file: File;
  id: string;
  ticketId?: string;
  progress?: boolean;
}

const MAX_PERCENT = 100;

const reorder = (list: IFile[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);

  result.splice(endIndex, 0, removed);

  return result;
};

const UploadSteps = () => {
  const [file, setFile] = useState<any>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string>("");
  const [fromUrl, setFromUrl] = useState<string>("");
  const [ticketId, setTicketId] = useState<any>(null);

  const [files, setFiles] = useState<IFile[]>([]);

  const [isUserVerified, setIsUserVerified] = useState<boolean | undefined>(false);

  const [sexuality, setSexuality] = useState<string>("straight");
  const [niches] = useState<any>([]);
  const [tags, setTags] = useState<any>(null);
  const [description, setDescription] = useState<string>("");
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [isEnabledSound, setIsEnabledSound] = useState<boolean>(true);
  const [duration, setDuration] = useState<number[]>([0, 0]);

  const [timeLineURL, setTimeLineURL] = useState("");

  const [isFetching, setIsFetching] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [progressPercent, setProgressPercent] = useState(0);

  const [createdId, setCreatedId] = useState<string>("");

  const navigate = useNavigate();

  const handlePrivate = () => {
    setIsPrivate((prev) => !prev);
  };

  const handleSound = () => {
    setIsEnabledSound((prev) => !prev);
  };

  const removeFileFromArray = (id: number) => {
    const newFiles = [...files];

    newFiles.splice(id, 1);
    setFiles(newFiles);
  };

  const next = () => {
    if (currentStep === 0 && fileType === "multiple") {
      let isUploaded = true;
      files.forEach((el: any) => {
        if (el.uploadProgress < MAX_PERCENT) {
          isUploaded = false;
        }
      });
      if (!isUploaded) {
        multiplePublish();
      } else {
        setCurrentStep(currentStep + 1);
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const resetState = () => {
    setFile(null);
    setFileUrl("");
    setFileType(null);
    setSexuality("straight");
    setTags(null);
    setTimeLineURL("");
    setFiles([]);
    setTicketId(null);
    setUploadError(null);
    setFromUrl("");
  };

  const back = () => {
    if (currentStep - 1 === -1) {
      resetState();
    } else if (currentStep - 1 >= 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleBackFromBulk = () => {
    resetState();
    setCurrentStep(0);
  };

  // Update gif settings after upload gif
  const updateGifSettings = async () => {
    setUploadError(null);
    setIsFetching(true);

    try {
      await apiMain.patchGif(createdId, {
        tags,
        sexuality,
        description,
        published: !isPrivate,
      });

      navigate(`/watch/${createdId}`);
    } catch (err) {
      setUploadError("Error uploading a file.");
    } finally {
      setIsFetching(false);
    }
  };

  const publish = async () => {
    setUploadError(null);
    setIsFetching(true);
    setProgressPercent(0);
    try {
      const id = await createGif({
        ticketId,
        file: file || null,
        fetchUrl: null,
        isPrivate: isPrivate,
        description: isUserVerified && !!description ? description : null,
        keepAudio: isEnabledSound,
        tags: tags,
        sexuality: sexuality,
        niches: niches,
        startTime: duration[0],
        endTime: duration[1]
      }, (sent: any) => {
        if (sent.percent < MAX_PERCENT) {
          setProgressPercent(sent.percent);
        }
      });
      setCreatedId(id);
    } catch (e) {
      setUploadError("Error uploading a file.");
    } finally {
      setIsFetching(false);
    }
  };

  const multiplePublish = async () => {
    setUploadError(null);
    setIsFetching(true);
    const ticketIds: any[] | ((prevState: string[]) => string[]) = [];
    const newFiles = [...files];

    try {
      await Promise.all(
        files.map((el: IFile, index: number) => {
          return upload(el.file, (sent: any) => {
            if (sent.percent <= MAX_PERCENT) {
              newFiles[index].uploadProgress = sent.percent;
              setFiles(newFiles);
            }
          }).then(response => {
            console.debug(`File uploaded: i=${index} n=${el.file.name} ticket=${response.id}`);

            ticketIds.push({ ticketId: response.id, order: index });
            return response.id;
          });
        })
      );

      setCurrentStep(currentStep + 1);

      const orderTicketsId = ticketIds.sort((a,b) => a.order - b.order)
        .map(ticket => ticket.ticketId);

      console.debug("Creating a gallery.", orderTicketsId);

      const id = await createGallery({
        files: orderTicketsId,
        tags: tags,
        niches: niches,
        publish: isPrivate,
        preferences: sexuality
      }, (sent: any) => {
        if (sent.percent < MAX_PERCENT) {
          setProgressPercent(sent.percent);
        }
      });

      setCreatedId(id);
    } catch (e) {
      setUploadError("Error uploading a file.");
    } finally {
      setIsFetching(false);
    }
  };

  const getTimeLineUrl = async () => {
    try {
      const { timeLineUrl, id } = await uploadFile(file, () => {});
      setTimeLineURL(timeLineUrl);
      setTicketId(id);
    } catch (e) {
      setUploadError("Error uploading a file.");
    }
  };

  const onDragEnd = ({ destination, source }: DropResult) => {
    // dropped outside the list
    if (!destination) {
      return;
    }

    const newItems = reorder(files, source.index, destination.index);

    setFiles(newItems);
  };

  const me = useMeQuery();

  useEffect(() => {
    if (me.status === "success" && me.data) {
      setIsUserVerified(me.data?.verified || false);
    }
  }, [me.status, me.data]);

  useEffect(() => {
    if (file && fileType === "video") {
      getTimeLineUrl();
    }
    // eslint-disable-next-line
  }, [file])

  return (
    <>
      {(file || files.length || fromUrl) ?
        <Content>
          {/* eslint-disable-next-line */}
          {currentStep !== 4 && <HorizontalLabelPositionBelowStepper currentStep={currentStep} stepsCount={4}/>}
          {currentStep === 0 && (
            <UploadFirstStep
              next={next}
              back={back}
              fileUrl={fileUrl}
              setFileUrl={setFileUrl}
              file={file}
              setFiles={setFiles}
              files={files}
              fileType={fileType}
              duration={duration}
              setDuration={setDuration}
              timeLineURL={timeLineURL}
              uploadError={uploadError}
              removeFileFromArray={removeFileFromArray}
              isFetching={isFetching}
              onDragEnd={onDragEnd}
              fromUrl={fromUrl}
              isEnabledSound={isEnabledSound}
              handleSound={handleSound}
              publish={publish}
            />
          )}
          {currentStep === 1 && <UploadSecondStep next={next} back={back} setSexuality={setSexuality} selectedSexuality={sexuality}/>}
          {/* {currentStep === 2 && <UploadThirdStep next={next} back={back} setSelectedNiches={setNiches} selectedNiches={niches}/>} */}
          {currentStep === 2 && (
            <UploadFourthStep
              next={next}
              back={back}
              setTags={setTags}
              selectedTags={tags}
              isUserVerified={isUserVerified}
              publish={publish}
              isFetching={isFetching}
              progressPercent={progressPercent}
              fileType={fileType}
            />
          )}
          {currentStep === 3 && (
            <UploadFifthStep
              back={back}
              description={description}
              setDescription={setDescription}
              isPrivate={isPrivate}
              handlePrivate={handlePrivate}
              isFetching={isFetching}
              progressPercent={progressPercent}
              uploadError={uploadError}
              fileType={fileType}
              isUserVerified={isUserVerified}
              updateGifSettings={updateGifSettings}
            />
          )}
          {currentStep === 4 && (
            <UploadBulkImgStep
              back={handleBackFromBulk}
              setFiles={setFiles}
              files={files}
            />
          )}
          {uploadError && <ErrorText>{uploadError}</ErrorText>}
        </Content>
        :
        <>
          <UploadSelectGifType
            setFromUrl={setFromUrl}
            setFileType={setFileType}
            setFile={setFile}
            setFileUrl={setFileUrl}
            fileUrl={fileUrl}
            setFiles={setFiles}
            setTimeLineURL={setTimeLineURL}
            setTicketId={setTicketId}
            setCurrentStep={setCurrentStep}
          />
        </>
      }
    </>
  );
};

export default UploadSteps;

const Content = styled("div")({
  width: "100%",
  paddingTop: "24px",
  display: "flex",
  flexDirection: "column"
});

const ErrorText = styled("p")({
  color: colors.redPink,
  textAlign: "center",
  margin: "1rem 0"
});
